export type WorkType = "sale" | "transfer" | "supply" | "inventory";

export type WorkSessionDraft = {
	batch: string;
	work_type: WorkType;
	owner_id: number;
	owner_name?: string;
	is_public: boolean;
	server_v: number;
	locked_by: number | null;
	locked_by_name?: string;
	locked_until?: string | null;
	created?: string | null;
	updated?: string | null;
	data: Record<string, unknown>;
};

export type WorkSessionMeta = {
	is_public?: boolean;
	server_v?: number;
	owner_id?: number;
	owner_name?: string;
	locked_by?: number | null;
	locked_by_name?: string;
	locked_until?: string | null;
};

export const reconcilePostedWorkSessionSuppressions = <
	T extends { id: string },
>(
	local: T[],
	remote: WorkSessionDraft[],
	suppressedIds: Iterable<string>,
): { local: T[]; remote: WorkSessionDraft[]; suppressedIds: string[] } => {
	const suppressed = new Set(suppressedIds);
	if (!suppressed.size) return { local, remote, suppressedIds: [] };

	const remoteIds = new Set(remote.map((draft) => draft.batch));
	return {
		// Suppressed drafts are omitted for this reconciliation cycle even when
		// the server no longer has them, so a stale local copy is never uploaded.
		local: local.filter((task) => !suppressed.has(task.id)),
		remote: remote.filter((draft) => !suppressed.has(draft.batch)),
		suppressedIds: [...suppressed].filter((id) => remoteIds.has(id)),
	};
};

const META_KEYS = [
	"is_public",
	"server_v",
	"owner_id",
	"owner_name",
	"locked_by",
	"locked_by_name",
	"locked_until",
] as const;

export const serializeWorkSessionTask = (task: Record<string, any>): Record<string, unknown> => {
	const skip = new Set(["total", "active", ...META_KEYS]);
	const payload: Record<string, unknown> = {};
	Object.keys(task).forEach((key) => {
		if (skip.has(key)) return;
		const value = task[key];
		if (typeof value === "function") return;
		payload[key] = value;
	});
	if (task.createdAt) {
		payload.createdAt = task.createdAt instanceof Date ? task.createdAt.toISOString() : task.createdAt;
	}
	if (task.updatedAt) {
		payload.updatedAt = task.updatedAt instanceof Date ? task.updatedAt.toISOString() : task.updatedAt;
	}
	return JSON.parse(JSON.stringify(payload));
};

export const applyWorkSessionDraft = <T,>(
	draft: WorkSessionDraft,
	revive: (raw: any) => T,
): T => {
	const data = draft.data && typeof draft.data === "object" ? draft.data : {};
	return revive({
		...data,
		id: draft.batch,
		is_public: Boolean(draft.is_public),
		server_v: Number(draft.server_v || 1),
		owner_id: draft.owner_id,
		owner_name: draft.owner_name || "",
		locked_by: draft.locked_by,
		locked_by_name: draft.locked_by_name || "",
		locked_until: draft.locked_until || null,
	});
};

const versionOf = (task: WorkSessionMeta) => Number(task.server_v || 0);

export const wasSyncedToServer = (task: WorkSessionMeta): boolean =>
	versionOf(task) > 0 || Boolean(task.owner_id);

/** Live lock held by someone other than this user. dump_session only sends locked_by while the lease is active. */
export const lockHeldByOther = (
	task: { locked_by?: number | null },
	userId?: number,
): boolean => {
	const holder = Number(task.locked_by || 0);
	if (holder < 1) return false;
	if (userId == null || Number(userId) < 1) return true;
	return holder !== Number(userId);
};

export const taskNeedsPush = (
	lastFingerprint: string | undefined,
	currentFingerprint: string,
	failed: boolean,
): boolean => {
	if (failed) return true;
	if (lastFingerprint === undefined) return true;
	return lastFingerprint !== currentFingerprint;
};

export const mergeWorkSessionTasks = <T extends { id: string } & WorkSessionMeta>(
	local: T[],
	remote: WorkSessionDraft[],
	revive: (raw: any) => T,
	isDirty?: (id: string) => boolean,
	viewerId?: number,
): { tasks: T[]; uploadIds: string[]; dropIds: string[] } => {
	const byId = new Map<string, { local?: T; remote?: WorkSessionDraft }>();
	local.forEach((task) => {
		byId.set(task.id, { ...(byId.get(task.id) || {}), local: task });
	});
	remote.forEach((draft) => {
		byId.set(draft.batch, { ...(byId.get(draft.batch) || {}), remote: draft });
	});

	const tasks: T[] = [];
	const uploadIds: string[] = [];
	const dropIds: string[] = [];

	byId.forEach(({ local: localTask, remote: remoteDraft }) => {
		if (localTask && !remoteDraft) {
			if (wasSyncedToServer(localTask)) {
				dropIds.push(localTask.id);
				return;
			}
			tasks.push(localTask);
			uploadIds.push(localTask.id);
			return;
		}
		if (!localTask && remoteDraft) {
			tasks.push(applyWorkSessionDraft(remoteDraft, revive));
			return;
		}
		if (localTask && remoteDraft) {
			const remoteNewer = versionOf(remoteDraft) > versionOf(localTask);
			const dirty = Boolean(isDirty?.(localTask.id));
			if (remoteNewer && !dirty) {
				const next = applyWorkSessionDraft(remoteDraft, revive);
				(next as T & { active?: boolean }).active = Boolean((localTask as T & { active?: boolean }).active);
				tasks.push(next as T);
				return;
			}
			const next = {
				...localTask,
				owner_id: remoteDraft.owner_id,
				owner_name: remoteDraft.owner_name,
				locked_by: remoteDraft.locked_by,
				locked_by_name: remoteDraft.locked_by_name,
				locked_until: remoteDraft.locked_until,
				server_v: dirty ? versionOf(localTask) : Math.max(versionOf(localTask), versionOf(remoteDraft)),
				// Only the owner may change is_public, so for everyone else the
				// server's value is authoritative. Preferring the local copy left a
				// collaborator's cached flag stuck: `??` only falls back on
				// null/undefined, so a stale `true` survived the owner turning
				// sharing off and every later push was rejected.
				is_public:
					viewerId !== undefined && Number(remoteDraft.owner_id) !== Number(viewerId)
						? remoteDraft.is_public
						: localTask.is_public ?? remoteDraft.is_public,
			};
			(next as T & { active?: boolean }).active = Boolean((localTask as T & { active?: boolean }).active);
			tasks.push(next as T);
			// Equal versions used to upload on every poll (`!remoteNewer`), which
			// raced the in-flight PUT, bumped server_v, and toasted a false
			// "updated on another device" conflict. Only dirty drafts need a write.
			if (dirty && !lockHeldByOther(remoteDraft, viewerId)) {
				uploadIds.push(localTask.id);
			}
		}
	});

	return { tasks, uploadIds, dropIds };
};

/** Which held lease to drop when the active task changes or the editor closes. */
export const leaseIdToRelease = (
	heldId: string | null,
	activeId: string | undefined,
	enabled: boolean,
): string | null => {
	if (!heldId) return null;
	if (!enabled) return heldId;
	if (activeId && heldId !== activeId) return heldId;
	return null;
};
